import {
  Form,
  PasswordField,
  SubmitButton,
  Text,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';

import { useRequestResetPassword } from '../_hooks/reset-password';
import { ResetPasswordFormSchema } from '../_models/reset-password';

type FormValues = z.infer<typeof ResetPasswordFormSchema>;

type Props = {
  targetUserKey: string;
  targetEmailAddress: string;
};

export const ResetPasswordForm: FC<Props> = ({
  targetUserKey,
  targetEmailAddress,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();

  const { mutateAsync: requestResetPasswordAsync } = useRequestResetPassword();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    resolver: zodResolver(ResetPasswordFormSchema),
  });

  const onSubmit = async ({ oneTimeCode, ...values }: FormValues) => {
    await requestResetPasswordAsync({
      ...values,
      otp: oneTimeCode,
      key: targetUserKey,
    });

    await Promise.all([
      openMessage('success', t('message-password-update-completed')),
      (async () => {
        navigate('/login');
      })(),
    ]);
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Form.Item>
        <Text
          text={t('input-one-time-code-and-new-password', {
            email: targetEmailAddress,
          })}
        />
      </Form.Item>

      <TextInputField
        label={t('one-time-code')}
        name="oneTimeCode"
        control={control}
        errors={errors}
      />

      <PasswordField
        label={t('new-password')}
        name="newPassword"
        control={control}
        errors={errors}
      />

      <Form.Item>
        <SubmitButton disabled={!isDirty} loading={isSubmitting} />
      </Form.Item>
    </Form>
  );
};
